// --- src/components/Profile.tsx ---
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { User, Lock, Save, Loader2 } from 'lucide-react';
import { Toast } from './Toast';

const Profile: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loadingName, setLoadingName] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Load current user data
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          // Try full_name first, then display_name, then username
          const name =
            user.user_metadata?.full_name ||
            user.user_metadata?.display_name ||
            user.user_metadata?.username ||
            '';
          setDisplayName(name);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, []);

  const handleUpdateName = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingName(true);

    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: displayName },
      });

      if (error) throw error;

      setToast({ message: 'Név sikeresen mentve', type: 'success' });
    } catch (error: any) {
      setToast({
        message: error.message || 'Hiba a név mentésekor',
        type: 'error',
      });
    } finally {
      setLoadingName(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (newPassword.length < 6) {
      setToast({ message: 'A jelszónak legalább 6 karakter hosszúnak kell lennie', type: 'error' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setToast({ message: 'A jelszavak nem egyeznek', type: 'error' });
      return;
    }

    setLoadingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setToast({ message: 'Jelszó sikeresen megváltoztatva', type: 'success' });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setToast({
        message: error.message || 'Hiba a jelszó megváltoztatásakor',
        type: 'error',
      });
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <div
      className="min-h-screen p-4 md:p-8"
      style={{ backgroundColor: 'var(--color-background, #f9fafb)' }}
    >
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          Profil & Biztonság
        </h1>

        {/* Toast Notification */}
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        {/* Grid Layout: 2 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card A: Personal Information */}
          <div className="bg-card rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/20 rounded-lg">
                <User className="text-primary" size={24} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Személyes adatok</h2>
            </div>

            <form onSubmit={handleUpdateName} className="space-y-4">
              <div>
                <label
                  htmlFor="displayName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Megjelenített név
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition"
                  placeholder="Add meg a neved"
                />
              </div>

              <button
                type="submit"
                disabled={loadingName}
                className="w-full bg-primary hover:bg-primary/90 text-white py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingName ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    <span>Mentés...</span>
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    <span>Mentés</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Card B: Change Password */}
          <div className="bg-card rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Lock className="text-green-600 dark:text-green-400" size={24} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Jelszó megváltoztatása
              </h2>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Új jelszó
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition"
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Jelszó megerősítése
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition"
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loadingPassword}
                className="w-full bg-primary hover:bg-primary/90 text-white py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingPassword ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    <span>Mentés...</span>
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    <span>Jelszó megváltoztatása</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
